/**
 * Pure maths for the AR wall preview prototype (/[locale]/ar-preview).
 *
 * Kept free of three.js and the DOM so it can be unit-tested and so the page
 * can import it without pulling the 3D libraries into the initial chunk.
 */

export interface ArParams {
  /** Print width in centimetres. */
  widthCm: number;
  /** Print height in centimetres. */
  heightCm: number;
  /** Stretcher depth in centimetres; 0 means flat paper. */
  depthCm: number;
  /** Side/frame colour as a CSS hex string, or null for the neutral wrap. */
  frame: string | null;
  /** Requested photo URL, unvalidated. */
  img: string | null;
}

const MIN_CM = 5;
const MAX_CM = 300;
const MAX_DEPTH_CM = 10;
/** Paper has no stretcher, but a zero-thickness box is a degenerate mesh. */
export const PAPER_THICKNESS_M = 0.003;

function num(raw: string | null, fallback: number, min: number, max: number): number {
  if (raw == null || raw.trim() === "") return fallback;
  const n = Number(raw.replace(",", "."));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/** Accepts `#rgb`, `#rrggbb`, or the same without `#` (handy in a URL). */
export function parseColour(raw: string | null): string | null {
  if (!raw) return null;
  const v = raw.trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(v) || /^[0-9a-f]{6}$/i.test(v)) return `#${v.toLowerCase()}`;
  const named: Record<string, string> = {
    black: "#1a1a1a",
    crna: "#1a1a1a",
    white: "#f5f5f2",
    bela: "#f5f5f2",
    oak: "#b98b5b",
    hrast: "#b98b5b",
    natural: "#c9a77c",
    naravna: "#c9a77c",
  };
  return named[v.toLowerCase()] ?? null;
}

export function parseArParams(get: (key: string) => string | null): ArParams {
  return {
    widthCm: num(get("w"), 60, MIN_CM, MAX_CM),
    heightCm: num(get("h"), 90, MIN_CM, MAX_CM),
    depthCm: num(get("depth"), 2, 0, MAX_DEPTH_CM),
    frame: parseColour(get("frame")),
    img: get("img"),
  };
}

/** Model dimensions in metres (glTF and USDZ are both metre-scaled). */
export function modelSizeMetres(p: Pick<ArParams, "widthCm" | "heightCm" | "depthCm">) {
  return {
    width: p.widthCm / 100,
    height: p.heightCm / 100,
    depth: p.depthCm > 0 ? p.depthCm / 100 : PAPER_THICKNESS_M,
  };
}

/**
 * Centre crop of a source image to the print's aspect ratio — what the print
 * does when the photo's aspect differs from the product's ("cover").
 */
export function coverCrop(
  srcW: number,
  srcH: number,
  targetW: number,
  targetH: number,
): { sx: number; sy: number; sw: number; sh: number } {
  const srcAspect = srcW / srcH;
  const targetAspect = targetW / targetH;
  if (srcAspect > targetAspect) {
    // Source is wider: keep the full height, trim the sides.
    const sw = srcH * targetAspect;
    return { sx: (srcW - sw) / 2, sy: 0, sw, sh: srcH };
  }
  // Source is taller (or equal): keep the full width, trim top and bottom.
  const sh = srcW / targetAspect;
  return { sx: 0, sy: (srcH - sh) / 2, sw: srcW, sh };
}

/**
 * Pixel size of the texture canvas: the print's aspect, longest side capped
 * (Quick Look on older iPhones drops textures much above 2048 px), and never
 * upscaled beyond the cropped source.
 */
export function textureSize(
  cropW: number,
  cropH: number,
  maxSide = 2048,
): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(cropW, cropH));
  return {
    width: Math.max(1, Math.round(cropW * scale)),
    height: Math.max(1, Math.round(cropH * scale)),
  };
}

/** "60 × 90 cm", with a decimal comma for fractional sizes. */
export function formatSize(widthCm: number, heightCm: number): string {
  const f = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ","));
  return `${f(widthCm)} × ${f(heightCm)} cm`;
}

/** Our object storage. Its images go through Next's optimizer (same origin, no CORS). */
export const STORAGE_HOST_SUFFIX = ".r2.cloudflarestorage.com";

/**
 * Turns the `img` parameter into a URL the page may draw into a canvas, or
 * null if it is not one of ours. Storage URLs are routed through
 * `/_next/image` so the browser reads them same-origin (an un-CORSed
 * cross-origin image would taint the canvas and the GLB export would fail),
 * and arrive resized instead of as a 6 MB original.
 */
export function resolveImageUrl(raw: string | null, origin: string): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin === origin) return url.pathname + url.search;
  if (url.protocol === "https:" && url.hostname.endsWith(STORAGE_HOST_SUFFIX)) {
    return `/_next/image?url=${encodeURIComponent(url.toString())}&w=2048&q=75`;
  }
  return null;
}

/** A crop in normalised source coordinates (0..1), as the canvas editor keeps it. */
export interface NormalisedCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** True for a crop that lies inside the photo and has an area. */
export function isUsableCrop(c: NormalisedCrop | null | undefined): c is NormalisedCrop {
  if (!c) return false;
  const vals = [c.x, c.y, c.width, c.height];
  if (!vals.every((v) => Number.isFinite(v))) return false;
  const eps = 1e-6;
  return (
    c.width > 0 &&
    c.height > 0 &&
    c.x >= -eps &&
    c.y >= -eps &&
    c.x + c.width <= 1 + eps &&
    c.y + c.height <= 1 + eps
  );
}

/**
 * The pixel rectangle of the photo that goes on the print's face.
 *
 * With the editor's crop (normalised, already the print's shape) that is
 * exactly what the customer framed; without one — the test page, or a crop
 * that does not make sense — it falls back to the centre crop the print
 * would get by default.
 */
export function faceSourceRect(
  srcW: number,
  srcH: number,
  targetW: number,
  targetH: number,
  crop?: NormalisedCrop | null,
): { sx: number; sy: number; sw: number; sh: number } {
  if (isUsableCrop(crop)) {
    const sx = Math.max(0, crop.x) * srcW;
    const sy = Math.max(0, crop.y) * srcH;
    return {
      sx,
      sy,
      sw: Math.min(crop.width * srcW, srcW - sx),
      sh: Math.min(crop.height * srcH, srcH - sy),
    };
  }
  return coverCrop(srcW, srcH, targetW, targetH);
}

/**
 * Average colour of RGBA pixel data as `#rrggbb`. Used for the sides of a
 * stretched canvas, which show the photo's own edge pulled around the frame.
 * Transparent pixels are ignored; no opaque pixel at all gives null.
 */
export function averageColour(data: ArrayLike<number>): string | null {
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if (data[i + 3] < 128) continue;
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    n += 1;
  }
  if (n === 0) return null;
  const hex = (v: number) => Math.round(v / n).toString(16).padStart(2, "0");
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}
